import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Search, X } from "lucide-react";

const CommandPalette = () => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ posts: [], users: [], clans: [], weapons: [], guides: [] });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!q.trim()) { setResults({ posts: [], users: [], clans: [], weapons: [], guides: [] }); return; }
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q } });
        setResults(data);
      } finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(timer);
  }, [q]);

  const go = useCallback((to) => { setOpen(false); setQ(""); navigate(to); }, [navigate]);
  if (!open) return null;

  const sections = [
    { key: "posts", label: "Posts", items: results.posts, render: (p) => ({ title: p.title, sub: `[${p.category}] · ${p.author_callsign}`, to: `/forum/${p.id}` }) },
    { key: "users", label: "Operators", items: results.users, render: (u) => ({ title: u.callsign, sub: `LVL ${u.stats?.level} · ${u.stats?.role || "—"}`, to: `/u/${u.public_no ?? u.id}` }) },
    { key: "clans", label: "Clans", items: results.clans, render: (c) => ({ title: `[${c.tag}] ${c.name}`, sub: `Region ${c.region}`, to: `/clans/${c.id}` }) },
    { key: "weapons", label: "Weapons", items: results.weapons, render: (w) => ({ title: w.name, sub: `[${w.category}] · DMG ${w.damage ?? "—"}`, to: `/database` }) },
    { key: "guides", label: "Guides", items: results.guides, render: (g) => ({ title: g.title, sub: `${g.author_callsign}`, to: `/guides` }) },
  ];
  const hasAny = sections.some((s) => s.items.length > 0);

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-start justify-center pt-[10vh] px-4" onClick={() => setOpen(false)}>
      <div data-testid="cmd-palette" className="w-full max-w-xl bg-[#13151B] border border-[#2A2E3D] tactical-frame hud-corners fade-in-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 p-4 border-b border-[#2A2E3D]">
          <Search className="w-5 h-5 text-[#D4A94A]" />
          <input
            autoFocus
            data-testid="cmd-palette-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search posts, operators, clans, weapons, guides…"
            className="flex-1 bg-transparent outline-none font-mono text-sm"
          />
          <button onClick={() => setOpen(false)}><X className="w-4 h-4 text-[#94A3B8]" /></button>
        </div>
        <div className="max-h-[60vh] overflow-auto p-2">
          {loading && <div className="p-4 text-xs font-mono text-[#64748B]">// SEARCHING…</div>}
          {!loading && q && !hasAny && <div className="p-4 text-xs font-mono text-[#64748B]">// NO RESULTS</div>}
          {sections.map((s) => s.items.length > 0 && (
            <div key={s.key} className="mb-2">
              <div className="overline px-2 py-1">{s.label}</div>
              {s.items.map((it, i) => {
                const r = s.render(it);
                return (
                  <button key={it.id || i} data-testid={`cmd-result-${s.key}-${i}`} onClick={() => go(r.to)}
                    className="w-full text-left px-3 py-2 hover:bg-[#0B0C0E] tactical-frame-sm flex items-center justify-between gap-2 group">
                    <div className="min-w-0 flex-1">
                      <div className="font-stencil font-bold uppercase text-sm truncate">{r.title}</div>
                      <div className="font-mono text-xs text-[#64748B] truncate">{r.sub}</div>
                    </div>
                    <span className="text-[10px] font-mono text-[#D4A94A] opacity-0 group-hover:opacity-100">↵</span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
        <div className="px-4 py-2 border-t border-[#2A2E3D] text-[10px] font-mono text-[#64748B] flex justify-between">
          <span>⌘K / CTRL+K to toggle</span>
          <span>ESC to close</span>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
